import React, { useState, useContext, useEffect } from 'react';
import { Button, TextField, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloudIcon from '@mui/icons-material/Cloud';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import { AuthContext } from './AuthContext';
import 'react-resizable/css/styles.css';

// eslint-disable-next-line import/first
const Logo = '/seabird_hero_logo.png';

const SpreadsheetLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [cellData, setCellData] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellSizes, setCellSizes] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please provide both email and password.');
      return;
    }

    try {
      await login(username.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Error processing your request. Please try again later.');
    }
  };

  const handleCellClick = (row, col) => {
    // Don't allow editing of header cells or row numbers, or if dragging
    if (row === 0 || col === 0 || isDragging) return;
    
    const cellKey = `${row}-${col}`;
    
    // Simply select the cell - TextField will show automatically
    setSelectedCell(cellKey);
    // Clear editing state since we're using selection for editing now
    setEditingCell(null);
  };

  const handleCellChange = (row, col, value) => {
    setCellData({
      ...cellData,
      [`${row}-${col}`]: value
    });
  };

  const handleCellBlur = () => {
    // Clear selection when clicking outside
    setSelectedCell(null);
    setEditingCell(null);
  };

  const handleResize = (row, col, event, { size }) => {
    setCellSizes({
      ...cellSizes,
      [`${row}-${col}`]: { width: size.width, height: size.height }
    });
  };

  const cellStyle = {
    border: '1px solid #d0d0d0',
    backgroundColor: '#f8f8f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#666',
    minHeight: '40px',
  };

  const dataCellStyle = {
    border: '1px solid #d0d0d0',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    minHeight: '40px',
    cursor: 'cell',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  };

  const highlightedCellStyle = {
    ...dataCellStyle,
    backgroundColor: '#fff3cd',
    border: '2px solid #ffc107',
    '&:hover': {
      backgroundColor: '#fff3cd',
    },
  };

  const selectedCellStyle = {
    ...dataCellStyle,
    border: '2px solid #0066cc',
    backgroundColor: '#e6f3ff',
    '&:hover': {
      backgroundColor: '#e6f3ff',
    },
  };

  const headerCellStyle = {
    ...cellStyle,
    backgroundColor: '#e6e6e6',
  };

  const EditableCell = ({ row, col, value, isHighlighted = false, isSpecial = false }) => {
    const cellKey = `${row}-${col}`;
    const isSelected = selectedCell === cellKey;
    const cellValue = cellData[cellKey] || value || '';

    const getCellStyle = () => {
      if (isHighlighted) return highlightedCellStyle;
      if (isSelected) return selectedCellStyle;
      return dataCellStyle;
    };

    // Always show TextField for selected cells (except special cells)
    const showTextField = isSelected && !isSpecial;

    return (
      <Box 
        sx={{
          ...getCellStyle(),
          position: 'relative',
        }}
        onClick={() => handleCellClick(row, col)}
      >
        {showTextField ? (
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            value={cellValue}
            onChange={(e) => handleCellChange(row, col, e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                handleCellBlur();
              }
            }}
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '14px', padding: '4px' }
            }}
            sx={{ 
              '& .MuiInput-root': { border: 'none', height: '100%' },
              '& input': { padding: '4px' }
            }}
          />
        ) : (
          <Typography sx={{ 
            fontSize: '14px', 
            width: '100%',
            padding: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {isSpecial ? value : cellValue}
          </Typography>
        )}
      </Box>
    );
  };

  const cols = ['', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];
  const rows = 25;

  return (
    <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* App Header */}
      <Box
        sx={{
          height: '60px',
          backgroundColor: '#0C5085',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          fontWeight: 'bold',
          fontFamily: "'Poppins', sans-serif",
          borderBottom: '2px solid #000',
          gap: 2,
        }}
      >
        <Box 
          sx={{
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3px'
          }}
        >
          <img 
            src={Logo} 
            alt="DAILEY Logo" 
            style={{ 
              width: '30px', 
              height: 'auto'
            }}
          />
        </Box>
        DAILEY FORMS
      </Box>

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '10px',
            textAlign: 'center',
            borderBottom: '1px solid #ffcdd2',
          }}
        >
          {error}
        </Box>
      )}

      {/* Spreadsheet Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `60px 1fr 200px repeat(${cols.length - 3}, 1fr)`,
          gridTemplateRows: `40px repeat(${rows}, 1fr)`,
          flex: 1,
          border: '2px solid #000',
          borderTop: 'none',
          fontFamily: "'Poppins', sans-serif",
          overflow: 'hidden',
        }}
      >
      {/* Column headers */}
      <Box sx={cellStyle}></Box>
      {cols.slice(1).map(col => (
        <Box key={col} sx={headerCellStyle}>{col}</Box>
      ))}

      {/* Generate all rows */}
      {Array.from({ length: rows }, (_, rowIndex) => {
        const rowNum = rowIndex + 1;
        return (
          <React.Fragment key={rowNum}>
            {/* Row number */}
            <Box sx={cellStyle}>{rowNum}</Box>
            
            {/* Generate cells for this row */}
            {cols.slice(1).map((col, colIndex) => {
              const colNum = colIndex + 1;
              
              // Special cells with content
              if (rowNum === 2 && colNum === 1) {
                return (
                  <EditableCell 
                    key={`${rowNum}-${colNum}`}
                    row={rowNum} 
                    col={colNum} 
                    value={
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#000' }}>
                        LOGIN
                      </Typography>
                    }
                    isSpecial={true}
                  />
                );
              }
              
              if (rowNum === 3 && colNum === 1) {
                return (
                  <EditableCell 
                    key={`${rowNum}-${colNum}`}
                    row={rowNum} 
                    col={colNum} 
                    value={
                      <Typography sx={{ color: '#000' }}>Username:</Typography>
                    }
                    isSpecial={true}
                  />
                );
              }
              
              if (rowNum === 3 && colNum === 2) {
                return (
                  <Box key={`${rowNum}-${colNum}`} sx={highlightedCellStyle}>
                    <TextField
                      fullWidth
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      variant="standard"
                      placeholder="Enter username"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: '14px' }
                      }}
                      sx={{ '& .MuiInput-root': { border: 'none' } }}
                    />
                  </Box>
                );
              }
              
              if (rowNum === 4 && colNum === 1) {
                return (
                  <EditableCell 
                    key={`${rowNum}-${colNum}`}
                    row={rowNum} 
                    col={colNum} 
                    value={
                      <Typography sx={{ color: '#000' }}>Password:</Typography>
                    }
                    isSpecial={true}
                  />
                );
              }
              
              if (rowNum === 4 && colNum === 2) {
                return (
                  <Box key={`${rowNum}-${colNum}`} sx={highlightedCellStyle}>
                    <TextField
                      fullWidth
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      variant="standard"
                      placeholder="Enter password"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: '14px' }
                      }}
                      sx={{ '& .MuiInput-root': { border: 'none' } }}
                    />
                  </Box>
                );
              }
              
              if (rowNum === 5 && colNum >= 1 && colNum <= 4) {
                if (colNum === 1) {
                  return (
                    <Box key={`${rowNum}-${colNum}`} sx={{ ...dataCellStyle, gridColumn: 'span 4' }}>
                      <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleLogin}
                        sx={{ 
                          height: '34px',
                          borderRadius: '4px',
                          textTransform: 'none',
                          fontSize: '16px'
                        }}
                      >
                        Submit
                      </Button>
                    </Box>
                  );
                } else if (colNum <= 4) {
                  return null; // Skip these cells as they're covered by the span
                }
              }
              
              // Footer text in bottom right cells
              if (rowNum === 13 && colNum === 18) {
                return (
                  <EditableCell 
                    key={`${rowNum}-${colNum}`}
                    row={rowNum} 
                    col={colNum} 
                    value={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '10px', fontWeight: 'bold' }}>
                        MADE WITH <FavoriteIcon sx={{ fontSize: '10px', color: '#e91e63' }} /> BY DAILEY
                      </Box>
                    }
                    isSpecial={true}
                  />
                );
              }
              
              if (rowNum === 14 && colNum === 18) {
                return (
                  <EditableCell 
                    key={`${rowNum}-${colNum}`}
                    row={rowNum} 
                    col={colNum} 
                    value={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '10px', fontWeight: 'bold' }}>
                        HOSTED ON DAILEY CLOUD <CloudIcon sx={{ fontSize: '10px', color: '#87CEEB' }} /> OVER BRISTOL, CT
                      </Box>
                    }
                    isSpecial={true}
                  />
                );
              }
              
              // Regular editable cells
              return (
                <EditableCell 
                  key={`${rowNum}-${colNum}`}
                  row={rowNum} 
                  col={colNum} 
                />
              );
            })}
          </React.Fragment>
        );
      })}
      </Box>

    </Box>
  );
};

export default SpreadsheetLogin;
